import { t } from "i18next";
import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AppConfig } from "@/common/appConfig";
import { toast } from "@/hooks/use-toast";
import { useAccountSettings } from "@/hooks/user/useAccountSettings";
import type { TranscriptionSchemaType } from "@/schemas/transcription.schema";
import { useAuthStore } from "@/store/auth.store";
import { useSettingsOptions } from "@/store/settingsOptions.store";
import { DefaultTemplateSetting } from "./components/DefaultTemplateSetting";
import { DictationMicrophoneSetting } from "./components/DictationMicrophoneSetting";
import { TranscriptionDefaultFormFields } from "./components/TranscriptionDefaultFormFields";

const TranscriptionSettings = (): JSX.Element => {
  const styles = [
    { value: "Bullet Point", label: t("noteOptions.style.bulletPoint") },
    { value: "Paragraph", label: t("noteOptions.style.paragraph") },
  ];
  const formats = [
    { value: "Detailed", label: t("noteOptions.format.detailed") },
    { value: "Concise", label: t("noteOptions.format.concise") },
  ];
  const { userData } = useAuthStore();
  const { updatePreferences, refreshAccount, loading, result } =
    useAccountSettings();

  const {
    visitRetentionPeriods,
    transcriptionLanguages,
    fetch: fetchSettingsOptions,
    loading: loadingSettingsOptions,
  } = useSettingsOptions();

  useEffect(() => {
    void fetchSettingsOptions();
  }, [fetchSettingsOptions]);

  const resetValues = useMemo(
    () => ({
      style: userData?.style ?? "",
      format: userData?.format ?? "",
      transcriptionLanguageId: userData?.userPreference.transcriptionLanguageId,
      transcriptionLanguage:
        transcriptionLanguages.find(
          (element) =>
            element.id === userData?.userPreference.transcriptionLanguageId,
        )?.name ?? "",
      visitRetentionPeriodId: userData?.userPreference.visitRetentionPeriodId,
      visitRetentionPeriod:
        visitRetentionPeriods.find(
          (element) =>
            element.id === userData?.userPreference.visitRetentionPeriodId,
        )?.label ?? "",
    }),
    [userData, transcriptionLanguages, visitRetentionPeriods],
  );

  const form = useForm<TranscriptionSchemaType>({
    shouldUnregister: false,
    defaultValues: resetValues,
  });

  useEffect(() => {
    if (result != null && !loading) {
      if (result) {
        toast({
          variant: "success",
          title: t("settings.transcription.updatedToast.successful.title"),
          description: t(
            "settings.transcription.updatedToast.successful.description",
          ),
        });
        void refreshAccount();
      } else {
        toast({
          variant: "error",
          title: t("settings.transcription.updatedToast.error.title"),
          description: t(
            "settings.transcription.updatedToast.error.description",
          ),
        });
        form.reset(resetValues);
      }
    }
  }, [result, loading, refreshAccount]);

  const handleChange = (data: Partial<TranscriptionSchemaType>): void => {
    if (userData) {
      void updatePreferences(data);
    }
  };

  useEffect(() => {
    form.reset(resetValues);
  }, [transcriptionLanguages, userData, form, resetValues]);

  return (
    <div
      className={
        AppConfig.enableAppRedesign
          ? "flex flex-col gap-5 sm:gap-4"
          : "space-y-8"
      }
    >
      <DefaultTemplateSetting />
      <DictationMicrophoneSetting />
      <FormProvider {...form}>
        <TranscriptionDefaultFormFields
          isSubmitting={loading}
          loading={loadingSettingsOptions}
          data={{
            transcriptionLanguages,
            formats,
            styles,
            visitRetentionPeriods,
          }}
          onChange={handleChange}
        />
      </FormProvider>
    </div>
  );
};

export default TranscriptionSettings;
